import React, { createContext, useCallback, useState } from 'react';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';

import { useFetch, useForm, useMutation, useSegment } from 'utils/hooks';
import { createFormData, createPayload } from './utils';
import { FORM_SCHEMA, DEFAULT_FORM_DATA } from './stubs';

/**
 * @type {React.Context<{
 *  data: import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>,
 *  initialData: import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>,
 *  isConfigurable: boolean,
 *  errors: Partial<import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>>,
 *  isDirty: boolean,
 *  isError: boolean,
 *  isLoading: boolean,
 *  isProcessing: boolean,
 *  onSave: () => Promise<any>,
 *  onReload: () => void
 *  onValidate: (updates: Partial<import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>>) => void,
 *  onChange: (updates: Partial<import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>>) => void,
 * }>}
 */
const PaymentProcessingFeeContext = createContext({});

const OPM_KEY = 'ORIGINAL_PAYMENT_METHOD';

export const usePaymentProcessingFee = () => {
  const context = React.useContext(PaymentProcessingFeeContext);
  if (!context) {
    throw new Error(
      'usePaymentProcessingFee must be used within a PaymentProcessingFeeProvider'
    );
  }
  return context;
};

export const PaymentProcessingFeeProvider = ({ children }) => {
  const { track } = useSegment();
  const [opmData, setOpmData] = useState(null);

  const {
    data,
    initialData,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA
  });

  const isOpmActive = _get(opmData, 'is_active', false);
  const isFeeAllowed = _get(opmData, 'fees.allow', false);
  const isConfigurable = isOpmActive && isFeeAllowed;

  const { isError, isLoading, fetchData: onReload } = useFetch(
    'configure.refundMethods.root',
    {
      onSuccess: data => {
        const list = _isArray(data) ? data : [];
        const opm = list.find(info => _get(info, 'payment_method') === OPM_KEY);
        if (opm) {
          setOpmData(opm);
          onSetInitialData(createFormData(opm));
        }
      }
    }
  );

  const { isProcessing, mutate } = useMutation(
    'configure.refundMethods.root',
    'PUT',
    {
      errorMessage: 'Unable to save Payment Processing Fee settings.',
      routeParam: _get(opmData, 'id'),
      payload: () => createPayload(data, opmData),
      onValidate: () => isConfigurable && onValidate(data),
      onSuccess: data => {
        if (data) {
          track('FTU Wizard Fee Configured', {
            fee_type: 'payment_processing_fee',
            fees_enabled: _get(data, 'fees.enabled', false),
            fee_type_value: _get(data, 'fees.fee_type', null),
            amount: _get(data, 'fees.amount', null),
            ceiling_amount: _get(data, 'fees.ceiling_amount', null)
          });
          setOpmData(data);
          onSetInitialData(createFormData(data));
        }
      }
    }
  );

  const onSave = useCallback(() => {
    return new Promise((resolve, reject) => {
      mutate({}, (err, data) => {
        if (err) reject(err);
        else resolve(data);
      });
    });
  }, [mutate]);

  return (
    <PaymentProcessingFeeContext.Provider
      value={{
        data,
        initialData,
        isConfigurable,
        errors,
        isDirty,
        isError,
        isLoading,
        isProcessing,
        onReload,
        onValidate,
        onChange,
        onSave
      }}
    >
      {children}
    </PaymentProcessingFeeContext.Provider>
  );
};
